import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Callout,
  DateField,
  IconTile,
  Modal,
  RadioButton,
  RadioGroup,
  SelectField,
  Switch,
  Tag,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  type SelectFieldOption,
} from '@humanforce/mango-ui-react';
import {
  createPayRun,
  listBusinessRecordReferences,
  listPayGroupReferences,
  listPayPeriodReferences,
  PayRunRequestError,
  getAdjustmentOptions,
} from './payRunsHttpClient';
import type {
  AdjustmentClassification,
  AdjustmentOptions,
  AdjustmentReason,
  BusinessRecordReference,
  CreatePayRunRequest,
  PayGroupReference,
  PayPeriodReference,
  PayRunFieldError,
} from './payRunsApiTypes';
import './NewPayRunModal.css';

export type NewPayRunCloseReason = 'cancelled' | 'created';

interface NewPayRunModalProps {
  open: boolean;
  onClose: (reason: NewPayRunCloseReason, createdPayId?: string) => void;
}

const STANDARD = 'standard' as const;
const adjustmentTypes: Array<{ value: AdjustmentClassification; label: string }> = [
  { value: 'back_pay', label: 'Back pay' },
  { value: 'separation', label: 'Separation' },
  { value: 'bonus_commission', label: 'Bonus-commission' },
  { value: 'adjustment', label: 'Adjustment' },
];
const adjustmentReasons: Array<{ value: AdjustmentReason; label: string }> = [
  { value: 'timesheets', label: 'Timesheets' },
  { value: 'leave', label: 'Leave' },
  { value: 'missed_pay', label: 'Missed pay' },
  { value: 'rate_change', label: 'Rate change' },
  { value: 'other', label: 'Other' },
];

type ReferenceLoadError = {
  source: 'business_records' | 'pay_groups' | 'pay_periods';
  message: string;
};

const referenceErrorTitles: Record<ReferenceLoadError['source'], string> = {
  business_records: 'Business Records unavailable',
  pay_groups: 'Pay Groups unavailable',
  pay_periods: 'Pay Periods unavailable',
};

const asSingleValue = (value: unknown | unknown[] | null): string =>
  typeof value === 'string' ? value : '';

const options = <T,>(
  values: T[],
  getValue: (value: T) => string,
  getLabel: (value: T) => string,
  isDisabled?: (value: T) => boolean,
): SelectFieldOption[] => values.map((value) => ({
  value: getValue(value),
  label: getLabel(value),
  disabled: isDisabled?.(value),
}));

const fieldMessage = (errors: PayRunFieldError[], field: string): string | undefined =>
  errors.find((error) => error.field === field)?.message;

const localToday = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const NewPayRunModal = ({ open, onClose }: NewPayRunModalProps) => {
  const [businessRecords, setBusinessRecords] = useState<BusinessRecordReference[]>([]);
  const [payGroups, setPayGroups] = useState<PayGroupReference[]>([]);
  const [payPeriods, setPayPeriods] = useState<PayPeriodReference[]>([]);
  const [runType, setRunType] = useState<'standard' | 'adjustment'>('standard');
  const [adjustmentOptions, setAdjustmentOptions] = useState<AdjustmentOptions | null>(null);
  const [adjustmentClassification, setAdjustmentClassification] = useState<AdjustmentClassification | ''>('');
  const [reasons, setReasons] = useState<AdjustmentReason[]>([]);
  const [reportingPeriodKind, setReportingPeriodKind] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [eftReleaseDate, setEftReleaseDate] = useState('');
  const [businessRecordId, setBusinessRecordId] = useState('');
  const [payGroupId, setPayGroupId] = useState('');
  const [payPeriodId, setPayPeriodId] = useState('');
  const [displayLabel, setDisplayLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [futurePay, setFuturePay] = useState(false);
  const [loadingBusinessRecords, setLoadingBusinessRecords] = useState(false);
  const [loadingPayGroups, setLoadingPayGroups] = useState(false);
  const [loadingPayPeriods, setLoadingPayPeriods] = useState(false);
  const [referenceError, setReferenceError] = useState<ReferenceLoadError | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<PayRunFieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const idempotencyRef = useRef<{ signature: string; key: string } | null>(null);
  const payGroupRequestSequence = useRef(0);
  const payPeriodRequestSequence = useRef(0);
  const runTypeRef = useRef<'standard' | 'adjustment'>('standard');

  const selectedPeriod = useMemo(
    () => payPeriods.find((period) => period.payPeriodId === payPeriodId) ?? null,
    [payPeriodId, payPeriods],
  );
  const adjustmentPeriod = adjustmentOptions?.options.find((period) => period.kind === reportingPeriodKind);
  const notesError = notes.length > 500 ? 'Notes must be 500 characters or fewer.' : fieldMessage(fieldErrors, 'notes');
  const selectedPayGroup = payGroups.find((group) => group.payGroupId === payGroupId) ?? null;
  const summaryPeriod = runType === 'standard' ? selectedPeriod : adjustmentPeriod;
  const summaryPaymentDate = runType === 'standard' ? selectedPeriod?.paymentDate : paymentDate;
  const summaryEftDate = runType === 'standard' ? selectedPeriod?.eftReleaseDate : eftReleaseDate;
  const standardEftIssue = selectedPeriod && (!selectedPeriod.eftReleaseDate
    ? 'This Pay Period has no EFT release date. Update its calendar before creating a Pay Run.'
    : selectedPeriod.eftReleaseDate > selectedPeriod.paymentDate
      ? 'EFT release date must be on or before Payment date.' : undefined);
  const adjustmentEftIssue = !eftReleaseDate ? 'EFT release date is required.'
    : paymentDate && eftReleaseDate > paymentDate ? 'EFT release date must be on or before Payment date.' : undefined;
  const today = localToday();
  const isFuturePeriod = (period: PayPeriodReference | null | undefined) => Boolean(period && period.periodStartDate > today);
  const availableStandardPeriods = useMemo(() => payPeriods
    .filter((period) => period.available)
    .sort((left, right) => left.periodStartDate.localeCompare(right.periodStartDate)), [payPeriods]);
  const defaultStandardPeriods = useMemo(() => {
    const current = availableStandardPeriods.filter((period) => period.periodStartDate <= today);
    const earliestFuture = availableStandardPeriods.find((period) => period.periodStartDate > today);
    return earliestFuture ? [...current, earliestFuture] : current;
  }, [availableStandardPeriods, today]);
  const visibleStandardPeriods = useMemo(() => {
    return futurePay ? availableStandardPeriods : defaultStandardPeriods;
  }, [availableStandardPeriods, defaultStandardPeriods, futurePay]);
  const valid = Boolean(businessRecordId && payGroupId && !notesError && (runType === 'standard'
    ? selectedPeriod?.available && !standardEftIssue
    : adjustmentClassification && reasons.length > 0 && adjustmentPeriod && paymentDate && eftReleaseDate && eftReleaseDate <= paymentDate));

  useEffect(() => {
    if (!open) return;
    const labelDialog = () => {
      for (const dialog of document.querySelectorAll('[role="dialog"]')) {
        if (dialog.querySelector('.mgo-modal__title')?.textContent === 'New pay run')
          dialog.setAttribute('aria-label', 'New pay run');
      }
    };
    labelDialog();
    const observer = new MutationObserver(labelDialog);
    observer.observe(document.body, { childList: true, subtree: true });
    let active = true;
    setBusinessRecords([]);
    setPayGroups([]);
    setPayPeriods([]);
    setLoadingPayGroups(false);
    setLoadingPayPeriods(false);
    setBusinessRecordId('');
    setPayGroupId('');
    setPayPeriodId('');
    setRunType('standard');
    runTypeRef.current = 'standard';
    setAdjustmentOptions(null);
    setAdjustmentClassification('');
    setReasons([]);
    setReportingPeriodKind('');
    setPaymentDate('');
    setEftReleaseDate('');
    setDisplayLabel('');
    setNotes('');
    setDetailsExpanded(false);
    setFuturePay(false);
    setReferenceError(null);
    setSubmitError(null);
    setFieldErrors([]);
    idempotencyRef.current = null;
    payGroupRequestSequence.current += 1;
    payPeriodRequestSequence.current += 1;
    setLoadingBusinessRecords(true);

    void listBusinessRecordReferences()
      .then((records) => {
        if (active) {
          setBusinessRecords(records);
          if (records[0]) selectBusinessRecord(records[0].businessRecordId, runTypeRef.current);
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setReferenceError({
            source: 'business_records',
            message: error instanceof Error ? error.message : 'Business Records could not be loaded.',
          });
        }
      })
      .finally(() => {
        if (active) setLoadingBusinessRecords(false);
      });

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [open]);

  function selectBusinessRecord(value: unknown | unknown[] | null, requestedRunType = runTypeRef.current) {
    const nextId = asSingleValue(value);
    setBusinessRecordId(nextId);
    setPayGroupId('');
    setPayPeriodId('');
    setPayGroups([]);
    setPayPeriods([]);
    setAdjustmentOptions(null);
    setEftReleaseDate('');
    setReferenceError(null);
    setSubmitError(null);
    setFieldErrors([]);
    const sequence = ++payGroupRequestSequence.current;
    payPeriodRequestSequence.current += 1;
    if (!nextId) return;

    setLoadingPayGroups(true);
    void listPayGroupReferences(nextId)
      .then((groups) => {
        if (sequence === payGroupRequestSequence.current) {
          setPayGroups(groups);
          if (groups[0]) selectPayGroup(groups[0].payGroupId, requestedRunType, nextId);
        }
      })
      .catch((error: unknown) => {
        if (sequence === payGroupRequestSequence.current) {
          setReferenceError({
            source: 'pay_groups',
            message: error instanceof Error ? error.message : 'Pay Groups could not be loaded.',
          });
        }
      })
      .finally(() => {
        if (sequence === payGroupRequestSequence.current) setLoadingPayGroups(false);
      });
  }

  function selectPayGroup(
    value: unknown | unknown[] | null,
    requestedRunType = runTypeRef.current,
    requestedBusinessRecordId = businessRecordId,
  ) {
    const nextId = asSingleValue(value);
    setPayGroupId(nextId);
    setPayPeriodId('');
    setPayPeriods([]);
    setAdjustmentOptions(null);
    setLoadingPayPeriods(false);
    setReferenceError(null);
    setSubmitError(null);
    setFieldErrors([]);
    const sequence = ++payPeriodRequestSequence.current;
    if (!requestedBusinessRecordId || !nextId) return;

    setLoadingPayPeriods(true);
    const request = requestedRunType === 'standard'
      ? listPayPeriodReferences(requestedBusinessRecordId, nextId, STANDARD)
      : getAdjustmentOptions(requestedBusinessRecordId, nextId);
    void request
      .then((result) => {
        if (sequence !== payPeriodRequestSequence.current) return;
        if (Array.isArray(result)) {
          setPayPeriods(result);
          setPayPeriodId(result.find((period) => period.available)?.payPeriodId ?? '');
        }
        else {
          setAdjustmentOptions(result);
          const selected = result.options.find((option) => option.payPeriodId === result.defaultPayPeriodId)
            ?? result.options[0];
          setReportingPeriodKind(selected?.kind ?? '');
          setPayPeriodId(selected?.payPeriodId ?? '');
          setPaymentDate(result.paymentDate.proposedPaymentDate ?? '');
          setEftReleaseDate('');
        }
      })
      .catch((error: unknown) => {
        if (sequence === payPeriodRequestSequence.current) {
          setReferenceError({
            source: 'pay_periods',
            message: error instanceof Error ? error.message : 'Pay Periods could not be loaded.',
          });
        }
      })
      .finally(() => {
        if (sequence === payPeriodRequestSequence.current) setLoadingPayPeriods(false);
      });
  }

  const submit = async () => {
    if (!valid || submittingRef.current) return;
    const request: CreatePayRunRequest = {
      businessRecordId,
      payGroupId,
      payPeriodId,
      runType,
      displayLabel: displayLabel.trim() || null,
      notes: notes.trim() || null,
      ...(runType === 'adjustment' ? {
        adjustmentClassification: adjustmentClassification as AdjustmentClassification,
        reasons,
        reportingPeriodKind: reportingPeriodKind as 'current_open' | 'previous_closed',
        paymentDate,
        eftReleaseDate,
      } : {}),
    };
    const signature = JSON.stringify(request);
    if (idempotencyRef.current?.signature !== signature)
      idempotencyRef.current = { signature, key: crypto.randomUUID() };

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    setFieldErrors([]);
    try {
      const created = await createPayRun(request, idempotencyRef.current.key);
      onClose('created', created.payId);
    } catch (error) {
      if (error instanceof PayRunRequestError) {
        setSubmitError(error.message);
        setFieldErrors(error.fieldErrors);
      } else {
        setSubmitError(error instanceof Error ? error.message : 'The Pay Run could not be created.');
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const cancel = () => {
    if (!submittingRef.current) onClose('cancelled');
  };

  const noBusinessRecords = !loadingBusinessRecords
    && referenceError?.source !== 'business_records'
    && businessRecords.length === 0;
  const noPayGroups = Boolean(
    businessRecordId
    && !loadingPayGroups
    && referenceError?.source !== 'pay_groups'
    && payGroups.length === 0,
  );
  const noAvailablePeriods = Boolean(
    runType === 'standard'
    && payGroupId
    && !loadingPayPeriods
    && referenceError?.source !== 'pay_periods'
    && payPeriods.every((period) => !period.available),
  );

  return (
    <Modal
      visible={open}
      header="New pay run"
      size="large"
      closable={!submitting}
      dismissableMask={!submitting}
      closeOnEscape={!submitting}
      onHide={cancel}
      testId="new-pay-run-dialog"
      className="new-pay-run-modal"
      footer={(
        <div className="new-pay-run-modal__footer">
          <div className="new-pay-run-modal__summary" aria-live="polite">
            <strong>{selectedPayGroup ? `${selectedPayGroup.name}${displayLabel.trim() ? ` - ${displayLabel.trim()}` : ''}` : 'Select a pay group'}</strong>
            {summaryPeriod && <span>{summaryPeriod.periodStartDate} – {summaryPeriod.periodEndDate}</span>}
            {summaryPaymentDate && <span><strong>Payment Date:</strong> {summaryPaymentDate} <Tag label={runType === 'adjustment' ? 'Adjustment run' : isFuturePeriod(selectedPeriod) ? 'Future pay' : 'Standard pay'} tone={runType === 'adjustment' ? 'highlight' : 'blue'} /></span>}
            {summaryEftDate && <span><strong>EFT release:</strong> {summaryEftDate}</span>}
          </div>
          <Button
            label={runType === 'adjustment' ? 'Create adjustment run' : 'Create pay run'}
            variant="primary"
            loading={submitting}
            disabled={!valid}
            onClick={() => void submit()}
          />
        </div>
      )}
    >
      <form className="new-pay-run-modal__form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        <p className="new-pay-run-modal__subtitle">Choose preferences for the next pay run</p>
        {(referenceError || submitError) && (
          <div className="new-pay-run-modal__errors" role="status">
            {referenceError && (
              <Callout tone="critical" title={referenceErrorTitles[referenceError.source]}>
                {referenceError.message}
              </Callout>
            )}
            {submitError && <Callout tone="critical" title="Pay Run not created">{submitError}</Callout>}
          </div>
        )}

        <div className="new-pay-run-modal__run-type-picker">
          <div className="new-pay-run-modal__run-type-icons" aria-hidden="true">
            <span><IconTile icon="payroll-dollar" color={runType === 'standard' ? 'corporate' : 'blue'} /></span>
            <span><IconTile icon="refresh" color="purple" /></span>
          </div>
          <RadioGroup
            name="runType"
            value={runType}
            direction="horizontal"
            testId="run-type-group"
            onChange={(value) => {
              const next = value as 'standard' | 'adjustment';
              runTypeRef.current = next;
              setRunType(next);
              setPayGroupId(''); setPayPeriodId(''); setPayPeriods([]); setAdjustmentOptions(null);
              setAdjustmentClassification(''); setReasons([]); setReportingPeriodKind(''); setPaymentDate(''); setEftReleaseDate('');
              if (businessRecordId) selectBusinessRecord(businessRecordId, next);
            }}
          >
            <RadioButton value="standard" label="Standard pay" helpText="Pay workers for a period" testId="run-type-standard" />
            <RadioButton value="adjustment" label="Adjustment run" helpText="Amend a paid run" testId="run-type-adjustment" />
          </RadioGroup>
        </div>

        <div className="new-pay-run-modal__body">
          <SelectField
            label="Business record"
            required
            value={businessRecordId || null}
            options={options(businessRecords, (record) => record.businessRecordId, (record) => record.displayName)}
            placeholder={loadingBusinessRecords ? 'Loading Business Records…' : 'Select a Business Record'}
            loading={loadingBusinessRecords}
            disabled={loadingBusinessRecords || referenceError?.source === 'business_records'}
            searchable={false}
            status={fieldMessage(fieldErrors, 'businessRecordId') ? 'critical' : ''}
            statusText={fieldMessage(fieldErrors, 'businessRecordId')}
            onChange={selectBusinessRecord}
          />

          <fieldset className="new-pay-run-modal__section">
            <legend>Pay group</legend>
            <ToggleButtonGroup mode="single" value={payGroupId || null} className="new-pay-run-modal__option-row" onChange={(value) => selectPayGroup(value)}>
              {payGroups.map((group) => <ToggleButton size="small" className="new-pay-run-modal__option" value={group.payGroupId} label={group.name} key={group.payGroupId} />)}
            </ToggleButtonGroup>
          </fieldset>

          {runType === 'adjustment' ? (
            <>
              <fieldset className="new-pay-run-modal__section new-pay-run-modal__section--period">
                <legend>Pay period (to fix)</legend>
                <ToggleButtonGroup mode="single" value={reportingPeriodKind || null} className="new-pay-run-modal__option-row" onChange={(kind) => { const period = adjustmentOptions?.options.find((item) => item.kind === kind); setReportingPeriodKind(kind); setPayPeriodId(period?.payPeriodId ?? ''); }}>
                  {(adjustmentOptions?.options ?? []).map((period) => (
                    <ToggleButton size="small" className="new-pay-run-modal__option" value={period.kind} key={period.kind}>
                      <span>{period.periodLabel || `${period.periodStartDate} – ${period.periodEndDate}`}</span>{period.tag && <Tag label={period.tag === 'paid' ? 'Paid' : period.tag} tone="positive" />}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </fieldset>
              <div className="new-pay-run-modal__payment-date">
                <DateField label="Payment date" dateFormat="ymd" value={paymentDate} onChange={(value) => setPaymentDate(typeof value === 'string' ? value : '')} fullWidth />
                <DateField label="EFT release date" dateFormat="ymd" value={eftReleaseDate} status={fieldMessage(fieldErrors, 'eftReleaseDate') || (eftReleaseDate && adjustmentEftIssue) ? 'critical' : undefined} statusText={fieldMessage(fieldErrors, 'eftReleaseDate') || (eftReleaseDate ? adjustmentEftIssue : undefined)} onChange={(value) => setEftReleaseDate(typeof value === 'string' ? value : '')} fullWidth />
              </div>
              <fieldset className="new-pay-run-modal__section">
                <legend>Type</legend>
                <ToggleButtonGroup mode="single" value={adjustmentClassification || null} className="new-pay-run-modal__chips" onChange={(value) => setAdjustmentClassification(value as AdjustmentClassification)}>{adjustmentTypes.map((type) => <ToggleButton size="small" className="new-pay-run-modal__chip" value={type.value} label={type.label} key={type.value} />)}</ToggleButtonGroup>
              </fieldset>
              <fieldset className="new-pay-run-modal__section">
                <legend>Reason</legend>
                <div className="new-pay-run-modal__chips">{adjustmentReasons.map((reason) => <ToggleButton size="small" className="new-pay-run-modal__chip" value={reason.value} label={reason.label} key={reason.value} checked={reasons.includes(reason.value)} onChange={(checked) => setReasons((current) => checked ? [...current, reason.value] : current.filter((value) => value !== reason.value))} />)}</div>
              </fieldset>
            </>
          ) : (
            <fieldset className="new-pay-run-modal__section new-pay-run-modal__section--period">
              <div className="new-pay-run-modal__period-heading"><legend>Pay period</legend><Switch label="Future pay" checked={futurePay} onChange={(checked) => { setFuturePay(checked); const nextVisible = checked ? availableStandardPeriods : defaultStandardPeriods; if (!nextVisible.some((period) => period.payPeriodId === payPeriodId)) setPayPeriodId(nextVisible[0]?.payPeriodId ?? ''); }} /></div>
              <ToggleButtonGroup mode="single" value={payPeriodId || null} className="new-pay-run-modal__option-row" onChange={(value) => { setPayPeriodId(value); setSubmitError(null); setFieldErrors([]); }}>
                {visibleStandardPeriods.map((period) => (
                  <ToggleButton size="small" className="new-pay-run-modal__option" value={period.payPeriodId} key={period.payPeriodId}>
                    <span>{period.periodLabel || `${period.periodStartDate} – ${period.periodEndDate}`}</span>
                    {isFuturePeriod(period) && <Tag label="Future" tone="blue" />}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </fieldset>
          )}

          {runType === 'standard' && (
            <div className="new-pay-run-modal__payment-date">
              <DateField label="Payment date" dateFormat="ymd" value={selectedPeriod?.paymentDate ?? ''} readOnly fullWidth />
              <DateField label="EFT release date" dateFormat="ymd" value={selectedPeriod?.eftReleaseDate ?? ''}
                status={fieldMessage(fieldErrors, 'eftReleaseDate') || standardEftIssue ? 'critical' : undefined}
                statusText={fieldMessage(fieldErrors, 'eftReleaseDate') || standardEftIssue || undefined} readOnly fullWidth />
            </div>
          )}

          {!detailsExpanded ? (
            <Button label="Add a display label or note" icon="add" variant="tertiary" size="small" className="new-pay-run-modal__add-details" onClick={() => setDetailsExpanded(true)} />
          ) : (
            <div className="new-pay-run-modal__details">
              <TextField label="Display label" value={displayLabel} status={fieldMessage(fieldErrors, 'displayLabel') ? 'critical' : undefined} statusText={fieldMessage(fieldErrors, 'displayLabel')} onChange={(event) => { setDisplayLabel(event.target.value); setSubmitError(null); setFieldErrors((errors) => errors.filter((error) => error.field !== 'displayLabel')); }} />
              <TextField label="Notes" placeholder="Anything for payroll reviewers" value={notes} status={notesError ? 'critical' : undefined} statusText={notesError} onChange={(event) => { setNotes(event.target.value); setSubmitError(null); setFieldErrors((errors) => errors.filter((error) => error.field !== 'notes')); }} />
            </div>
          )}

          {noBusinessRecords && <Callout tone="neutral">No active Business Records with Pay Groups are available.</Callout>}
          {noPayGroups && <Callout tone="neutral">This Business Record has no active Pay Groups.</Callout>}
          {noAvailablePeriods && <Callout tone="neutral">This Pay Group has no available Pay Periods for a Standard Pay Run.</Callout>}
          {runType === 'adjustment' && adjustmentOptions?.unavailableReason && (
            <Callout tone="caution" title="Adjustment Pay Run unavailable">
              {adjustmentOptions.unavailableReason === 'adjustment_unavailable'
                ? 'Adjustment Pay Runs are temporarily unavailable while payroll storage is being updated.'
                : 'This Pay Group does not have a current open Standard period.'}
            </Callout>
          )}
        </div>
      </form>
    </Modal>
  );
};
